// chartExport.js
// The one path from a chart on screen to a PNG on disk.
//
// A chart's figure — its plotly traces and layout — is built by a pure function
// in its renderer (`buildBarFigure` and friends). The export restyles that same
// figure as a print (`buildExportLayout`: white paper, title band, print type)
// and hands it to plotly as a FIGURE OBJECT: plotly creates, draws and
// snapshots its own off-screen element. Plotly removes that element only once
// the snapshot has been taken — a render that fails leaves it behind — so every
// export here notes the off-screen elements present before it starts and, if it
// fails, removes the ones that appeared since; exports run one at a time so
// those can only be its own (see `withStrayCleanup`).
//
// A combined chart has no single plotly figure. Its leaves are each snapshotted
// the same way, then tiled onto one canvas — the same min(3, ceil(√n)) column
// grid the emailed PNG uses — under the parent title, and saved as one file.
import Plotly from 'plotly.js/dist/plotly';
import { CHART_SURFACE_RING, EXPORT_FONT, EXPORT_INK, EXPORT_PAPER, buildExportLayout } from './chartTheme';

export const EXPORT_SIZE = Object.freeze({ width: 1200, height: 800, scale: 2 });

// "Portfolio — Premium by class": the title printed on the bitmap, the chart's
// own title prefixed by the titles of the combined charts it sits inside.
export const exportTitle = (ancestorTitles, title) => (
  [...(ancestorTitles || []), title]
    .filter((part) => typeof part === 'string' && part.trim() !== '')
    .map((part) => part.trim())
    .join(' — ')
);

// "portfolio-premium-by-class"; a chart with no words to its name is "chart".
export const exportFilename = (title) => {
  const slug = String(title || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'chart';
};

// Marks that were tuned for the dark card, retuned for paper: the hairline
// between touching fills becomes white (the surface it now sits on), and
// lines and points gain a little weight so they survive scaling down in a
// slide or a document.
const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

export const styleTracesForExport = (data) => (data || []).map((trace) => {
  const next = { ...trace };
  if (isPlainObject(trace.marker)) {
    next.marker = { ...trace.marker };
    if (isPlainObject(trace.marker.line) && trace.marker.line.color === CHART_SURFACE_RING) {
      next.marker.line = { ...trace.marker.line, color: EXPORT_PAPER };
    }
    if (trace.type === 'scatter' && typeof trace.marker.size === 'number') {
      next.marker.size = trace.marker.size + 2;
    }
  }
  if (trace.type === 'pie') {
    // Slice labels at print size, white on the fill, ink outside it.
    next.textfont = { ...(trace.textfont || {}), size: 16 };
    next.insidetextfont = { ...(trace.insidetextfont || {}), size: 16, color: EXPORT_PAPER };
    next.outsidetextfont = { ...(trace.outsidetextfont || {}), size: 16, color: EXPORT_INK };
  }
  if (trace.type === 'scatter' && isPlainObject(trace.line) && typeof trace.line.width === 'number') {
    next.line = { ...trace.line, width: trace.line.width + 1 };
  }
  return next;
});

export const buildExportFigure = ({ data, layout, title }) => ({
  data: styleTracesForExport(data),
  layout: buildExportLayout(layout, { title }),
});

// Plotly's export clone: a <div> appended to <body>, absolutely positioned at
// left -5000px (plot_api/to_image.js). Nothing else in the app sits there.
const offscreenPlots = () => Array.from(document.body.children).filter((element) => (
  element.tagName === 'DIV' && element.style.position === 'absolute' && element.style.left === '-5000px'
));

// Exports run one at a time. The sweep below removes whatever off-screen
// element appeared after the export began, and that is only safe to call
// "mine" when no other export could have started in the meantime — otherwise
// one chart's failure would take a neighbouring chart's render down with it.
let exportQueue = Promise.resolve();

const withStrayCleanup = (work) => {
  const run = exportQueue.then(async () => {
    const before = new Set(offscreenPlots());
    try {
      return await work();
    } catch (err) {
      offscreenPlots().filter((element) => !before.has(element)).forEach((element) => {
        try { Plotly.purge(element); } catch (purgeErr) { /* never plotted: nothing to purge */ }
        element.remove();
      });
      throw err;
    }
  });
  // The queue itself never rejects; each caller sees its own outcome.
  exportQueue = run.catch(() => undefined);
  return run;
};

const imageOptions = (scale = EXPORT_SIZE.scale) => ({
  format: 'png',
  width: EXPORT_SIZE.width,
  height: EXPORT_SIZE.height,
  scale,
});

// One chart to one PNG. Rejects when plotly cannot render or the browser
// refuses the download; the caller says so.
export async function downloadChartPng({ data, layout, title, filename }) {
  await withStrayCleanup(() => Plotly.downloadImage(buildExportFigure({ data, layout, title }), {
    ...imageOptions(),
    filename: exportFilename(filename || title),
  }));
}

// Tiles per row for a combined chart of `count` leaves: 1, 2, 2, 2, 3, 3, …
export const compositeColumns = (count) => Math.max(1, Math.min(3, Math.ceil(Math.sqrt(count))));

const loadImage = (src) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('A rendered chart image could not be decoded.'));
  image.src = src;
});

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// The whole of a combined chart as one PNG: every leaf rendered as its own
// print, laid out in a grid under the parent's title. `leaves` are
// `{ data, layout, title }` in reading order; each tile's title is the leaf's
// ancestry below the parent, so the parent's words appear once, at the top.
export async function downloadCompositePng({ title, leaves, filename }) {
  if (!Array.isArray(leaves) || leaves.length === 0) {
    throw new Error('This combined chart has nothing to export.');
  }
  // Nine tiles at double resolution is a 7200 × 4800 canvas; past four tiles
  // the print drops to single resolution, which is still 1200 × 800 per tile.
  const scale = leaves.length > 4 ? 1 : EXPORT_SIZE.scale;
  const urls = await withStrayCleanup(() => Promise.all(
    leaves.map((leaf) => Plotly.toImage(buildExportFigure(leaf), imageOptions(scale))),
  ));
  const images = await Promise.all(urls.map(loadImage));

  const columns = compositeColumns(images.length);
  const rows = Math.ceil(images.length / columns);
  const tileWidth = EXPORT_SIZE.width * scale;
  const tileHeight = EXPORT_SIZE.height * scale;
  const gap = 32 * scale;
  const heading = typeof title === 'string' ? title.trim() : '';
  const band = heading ? 72 * scale : 0;

  const canvas = document.createElement('canvas');
  canvas.width = columns * tileWidth + (columns + 1) * gap;
  canvas.height = band + rows * tileHeight + (rows + 1) * gap;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('The browser could not draw the combined image.');

  context.fillStyle = EXPORT_PAPER;
  context.fillRect(0, 0, canvas.width, canvas.height);
  if (heading) {
    context.fillStyle = EXPORT_INK;
    context.font = `600 ${26 * scale}px ${EXPORT_FONT}`;
    context.textBaseline = 'middle';
    context.fillText(heading, gap, gap + band / 2);
  }
  context.strokeStyle = 'rgba(0, 0, 0, 0.10)';
  context.lineWidth = Math.max(1, scale);
  images.forEach((image, index) => {
    const x = gap + (index % columns) * (tileWidth + gap);
    const y = band + gap + Math.floor(index / columns) * (tileHeight + gap);
    context.drawImage(image, x, y, tileWidth, tileHeight);
    context.strokeRect(x + 0.5, y + 0.5, tileWidth - 1, tileHeight - 1);
  });

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('The browser could not produce the image file.');
  downloadBlob(blob, `${exportFilename(filename || title || 'combined chart')}.png`);
}
